import type { DraftAccount } from '@interfaces/account';
import { AccountStates, AccountTypes } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import { AffiliationRelations } from '@interfaces/affiliationRelations';
import { AffiliationTypes } from '@interfaces/affiliationTypes';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { ClientSelectField } from '@ovComponents/2-component/clientSelectField/clientSelect';
import { Form } from '@ovComponents/2-component/form/form';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import {
  GoalLinkingInput,
  getValidGoals,
} from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import { Box, Button, Grid, Typography } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type JointCashDialogContentInterface = {
  userData: any;
  showGoalLinkingSection: boolean;
  onAccountCreated: (account: DraftAccount) => void;
  onDialogClose: () => void;
};

const initAffiliation: Partial<Affiliation> = {
  user: undefined,
  relation: undefined,
  type: AffiliationTypes.Joint,
};

export const JointCashDialogContent = ({
  userData,
  showGoalLinkingSection,
  onAccountCreated,
  onDialogClose,
}: JointCashDialogContentInterface) => {
  const { t } = useTranslation(['workflowCompletions', 'affiliationTypes']);
  const [jointHolder, setJointHolder] =
    useState<Partial<Affiliation>>(initAffiliation);
  const [goalsLinked, setGoalsLinked] = useState<Goal[]>([]);
  const goals = getValidGoals(userData);
  const isSubmissionDisabled =
    !jointHolder.user || !jointHolder.relation || !jointHolder.type;
  const submit = () => {
    onAccountCreated({
      type: AccountTypes.CASH_JOINT,
      applyForGovFunds: [],
      id: '',
      state: AccountStates.ACTIVE,
      user: userData,
      affiliations: [jointHolder as Affiliation],
      goalsLinked,
    });
  };
  return (
    <Form onSubmit={submit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Box
          onClick={onDialogClose}
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1 }}>
        {t('workflowCompletions:jointCashCreation.title')}
      </Typography>
      <Typography variant='headingXSmall' sx={{ mt: 3 }}>
        {t('workflowCompletions:jointAccountHolder.label')}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mt: 1 }}>
        {t('workflowCompletions:jointAccountHolder.subLabel')}
      </Typography>
      <Grid
        container
        spacing={1}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Grid size={8}>
          <ClientSelectField
            label={t('workflowCompletions:jointCashCreation.name')}
            user={jointHolder.user as any}
            setUser={(item) =>
              setJointHolder((prevState) => ({ ...prevState, user: item }))
            }
            fullWidth
            labelSx={{ mt: 3 }}
          />
        </Grid>
        <Grid size={4}>
          <SelectField
            sx={{ mt: 3 }}
            onChange={(e: any) =>
              setJointHolder((prevState) => ({
                ...prevState,
                relation: e.target.value,
              }))
            }
            disabled={false}
            label={t('workflowCompletions:relationship')}
            fullWidth
            value={jointHolder.relation}
            error={false}
          >
            {Object.values(AffiliationRelations)
              .filter(
                (item) => item !== 'PRIMARY_CAREGIVER' && item !== 'GUARDIAN',
              )
              .map((relation, index) => (
                <MenuItem key={index} value={relation}>
                  {t(`affiliationTypes:affiliationRelationship.${relation}`)}
                </MenuItem>
              ))}
          </SelectField>
        </Grid>
      </Grid>
      {showGoalLinkingSection && (
        <Box sx={{ mt: 4 }}>
          <GoalLinkingInput
            goals={goals}
            goalsLinked={goalsLinked}
            setGoalsLinked={setGoalsLinked}
          />
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button
          color='primary'
          disabled={isSubmissionDisabled}
          sx={{ mt: 3 }}
          type='submit'
        >
          {t('workflowCompletions:jointCashCreation.addAccount')}
        </Button>
      </Box>
    </Form>
  );
};

export default JointCashDialogContent;
